'use client';
import { jsx } from '@emotion/react/jsx-runtime';
import { useState, useMemo, useCallback } from 'react';
import { useDebouncedState, useDidUpdate } from '@s7/hooks';
import { composeHandlers } from '@s7/react-utils';
import { groupBy, isString, deepMerge } from '@s7/utils';
import { useFooterAnalytics } from '../../analytics.js';
import { useSearchQuery } from '../../../domain/services/SearchService/useSearchQuery.js';
import { filterNavigationItems } from './utils/filterNavigationItems.js';
import { SearchData } from '../../../domain/model/SearchData.js';
import { NavigationBlock } from '../../../domain/model/FooterData/blocks/Navigation.js';
import { sliceItems } from './utils/sliceItems.js';
import { FooterSearch } from '../../../../Footer/components/Search/FooterSearch.js';
import { compileTemplate } from '../../../../common/compileTemplate/compileTemplate.js';
import { FooterSearchFallback } from '../../../../Footer/components/Search/slots/FooterSearchFallback/FooterSearchFallback.js';
import { composeOptions } from '../utils/composeOptions.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var defaultServiceOptions = {
    limit: 10,
    lang: "ru"
};
var ITEM_CATEGORY = "search";
var SearchContainer = function(props) {
    var _content_helpCenter;
    var service = props.service, content = props.content, handlers = props.handlers, mainNavigation = props.mainNavigation, productNavigation = props.productNavigation, others = _object_without_properties_loose(props, [
        "service",
        "content",
        "handlers",
        "mainNavigation",
        "productNavigation"
    ]);
    var _ref = handlers || {}, onSuccess = _ref.onSuccess, onError = _ref.onError, onFinish = _ref.onFinish, onShowMoreClick = _ref.onShowMoreClick, onShowMoreSelect = _ref.onShowMoreSelect, onItemClick = _ref.onItemClick, onItemSelect = _ref.onItemSelect, onNavigationItemSelect = _ref.onNavigationItemSelect, onNavigationItemClick = _ref.onNavigationItemClick, onInputReset = _ref.onInputReset;
    var _useDebouncedState = useDebouncedState("", 300), debouncedValue = _useDebouncedState[0], setDebouncedValue = _useDebouncedState[1];
    var _useState = useState(false), showLoading = _useState[0], setShowLoading = _useState[1];
    /// Query
    var _useSearchQuery = useSearchQuery(_extends({}, defaultServiceOptions, service, {
        query: debouncedValue
    }), {
        onSuccess: onSuccess,
        onError: onError,
        onFinish: function() {
            onFinish == null ? void 0 : onFinish();
            setShowLoading(false);
        }
    }), data = _useSearchQuery.data, error = _useSearchQuery.error, refresh = _useSearchQuery.refresh, reset = _useSearchQuery.reset, loading = _useSearchQuery.loading;
    /// Data
    var _useMemo = useMemo(function() {
        if (error) {
            return {
                helpArticles: [],
                otherData: []
            };
        }
        return groupBy(data, function(item) {
            var _item_data;
            switch(item == null ? void 0 : (_item_data = item.data) == null ? void 0 : _item_data.entityType){
                case "help_articles":
                    return "helpArticles";
                default:
                    return "otherData";
            }
        });
    }, [
        data
    ]), helpArticles = _useMemo.helpArticles, otherData = _useMemo.otherData;
    var navigationItems = useMemo(function() {
        return filterNavigationItems(NavigationBlock.toSearchNavigationItems(mainNavigation, productNavigation), SearchData.toSearchNavigationItems(otherData), debouncedValue);
    }, [
        mainNavigation,
        productNavigation,
        debouncedValue,
        otherData
    ]);
    var helpCenterItems = useMemo(function() {
        var _content_helpCenter;
        return sliceItems(SearchData.toHelpCenterItems(helpArticles), content == null ? void 0 : (_content_helpCenter = content.helpCenter) == null ? void 0 : _content_helpCenter.maxItems);
    }, [
        helpArticles,
        content == null ? void 0 : (_content_helpCenter = content.helpCenter) == null ? void 0 : _content_helpCenter.maxItems
    ]);
    var showMore = (content || {}).showMore;
    var _ref1 = showMore || {}, tmp = _ref1.link, url = (tmp === void 0 ? {
        url: undefined
    } : tmp).url;
    var _showMore = useMemo(function() {
        return showMore && isString(url) ? deepMerge(showMore, {
            link: {
                url: compileTemplate(url, {
                    query: debouncedValue
                })
            }
        }) : showMore;
    }, [
        showMore,
        debouncedValue
    ]);
    /// Analytics
    var sendEvent = useFooterAnalytics();
    var dispatchClickEvent = function(param) {
        var id = param.id;
        sendEvent("click_cta", {
            item_category: ITEM_CATEGORY,
            item_name: String(id)
        });
    };
    var dispatchShowMoreEvent = function() {
        dispatchClickEvent({
            id: "show_more"
        });
    };
    var dispatchInputResetEvent = function() {
        dispatchClickEvent({
            id: "reset"
        });
    };
    var dispatchSearchSuccessEvent = function() {
        sendEvent("search", {
            item_category: ITEM_CATEGORY,
            search_term: debouncedValue,
            quantity: (helpCenterItems == null ? void 0 : helpCenterItems.length) + (navigationItems == null ? void 0 : navigationItems.length)
        });
    };
    ///
    useDidUpdate(function() {
        if (!loading) {
            dispatchSearchSuccessEvent();
        }
    }, [
        loading,
        data,
        navigationItems
    ]);
    useDidUpdate(function() {
        if (debouncedValue.length >= 3) {
            refresh();
        }
    }, [
        debouncedValue
    ]);
    var handleInputChange = useCallback(function(value) {
        reset();
        setShowLoading(value.length >= 3);
        setDebouncedValue(value);
    }, []);
    return /*#__PURE__*/ jsx(FooterSearch, _extends({}, others, content, {
        helpCenter: _extends({}, content == null ? void 0 : content.helpCenter, {
            items: helpCenterItems
        }),
        navigation: _extends({}, content == null ? void 0 : content.navigation, {
            items: navigationItems
        }),
        renderSearchFallback: function() {
            return /*#__PURE__*/ jsx(FooterSearchFallback, {
                description: "Проверьте запрос и попробуйте ещё раз",
                title: "Ничего не найдено"
            });
        },
        loading: showLoading,
        showMore: _showMore,
        onInputReset: composeHandlers(onInputReset, dispatchInputResetEvent),
        onItemClick: composeHandlers(onItemClick, dispatchClickEvent, composeOptions),
        onItemSelect: composeHandlers(onItemSelect, dispatchClickEvent, composeOptions),
        onNavigationItemClick: composeHandlers(onNavigationItemClick, dispatchClickEvent, composeOptions),
        onNavigationItemSelect: composeHandlers(onNavigationItemSelect, dispatchClickEvent, composeOptions),
        onShowMoreClick: composeHandlers(onShowMoreClick, dispatchShowMoreEvent),
        onShowMoreSelect: composeHandlers(onShowMoreSelect, dispatchShowMoreEvent),
        onChange: handleInputChange
    }));
};

export { SearchContainer };
